import {
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import { Dialog, DialogCloseProps } from '@/components/atoms';
import { cn } from '@/lib/utils/tailwind';

import { AsElement } from '@/types';

type SheetCloseProps<T extends ElementType = 'button'> = DialogCloseProps<T>;

const SheetCloseBase = forwardRef<
  HTMLElement,
  {
    as?: ElementType;
    className?: string;
    children?: ReactNode;
  } & Omit<HTMLAttributes<HTMLElement>, 'ref' | 'className' | 'children'>
>(({ as, className, children, ...props }, ref) => {
  const Component: ElementType = as || 'button';

  return (
    <Dialog.Close
      as={Component}
      className={cn(
        [
          'cursor-pointer',
          'rounded-full',
          'hover:bg-muted',
          'text-(--color-text-primary)',
        ],
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
    </Dialog.Close>
  );
});
SheetCloseBase.displayName = 'SheetClose';

const SheetClose = (<T extends ElementType = 'button'>(
  props: SheetCloseProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
): ReactElement => {
  return <SheetCloseBase {...props} />;
}) as <T extends ElementType = 'button'>(
  props: SheetCloseProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
) => ReactElement;

Object.defineProperty(SheetClose, 'displayName', {
  value: 'SheetClose',
  configurable: true,
});

export { SheetClose, type SheetCloseProps };
